// pages/TopicFiles.js
import { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, useLocation, Link } from 'react-router-dom';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';

// NEW: docx-preview
import { renderAsync } from 'docx-preview';
//import 'docx-preview/dist/docx-preview.css';

const LABELS = {
  PREP: 'הכנת הסוגיה',
  TOOLS: 'כלים להוראה',
  EVAL: 'הערכה ומשחוק',
  VISUAL_TALMUD: 'אמצעים חזותיים - תרשימים, תמונות וסרטונים',
  TOSFOT_IYYUN: 'תוספות וסוגיות בעיון',
  SIX_ACHIEVEMENTS: 'ששת ההישגים',
};

/** Build a preview URL depending on file type. */
function buildPreviewUrl(fileUrl) {
  const absolute = new URL(fileUrl, window.location.origin).href;
  const clean = absolute.split('#')[0];
  const ext = clean.split('.').pop()?.toLowerCase() || '';

  if (ext === 'pdf')  return { src: absolute, kind: 'pdf' };
  if (ext === 'docx') return { src: absolute, kind: 'docx' };

  // Legacy .doc (or others): no in-browser renderer here → open/download only
  return { src: absolute, kind: 'raw' };
}

/** ---------- DOCX preview area (renders into a div) ---------- */
// at top of file (recommended so the DOCX looks decent)
// import 'docx-preview/dist/docx-preview.css';

function DocxPreview({ url }) {
  const hostRef = useRef(null);
  const [status, setStatus] = useState({ loading: true, error: '' });

  useEffect(() => {
    let cancelled = false;
    const aborter = new AbortController();

    (async () => {
      try {
        setStatus({ loading: true, error: '' });

        // 1) Fetch the DOCX first (don’t depend on the ref yet)
        const res = await fetch(url, {
          signal: aborter.signal,
          // If your DOCX is on same origin and public, you can omit credentials entirely.
          // credentials: 'same-origin',
        });
        if (!res.ok) throw new Error('Failed to fetch DOCX');
        const arrayBuffer = await res.arrayBuffer();
        if (cancelled) return;

        // 2) Ensure the ref is mounted (wait a frame if needed)
        if (!hostRef.current) {
          await new Promise(requestAnimationFrame);
          if (cancelled) return;
        }
        const container = hostRef.current;
        if (!container) return;

        // 3) Clear previous render
        container.innerHTML = '';

        // 4) Render DOCX → HTML
        await renderAsync(arrayBuffer, container, null, {
          className: 'docx',
          inWrapper: true,
          ignoreWidth: true,
          ignoreHeight: true,
          breakPages: false,
          useBase64URL: true,
        });

        if (cancelled) return;
        setStatus({ loading: false, error: '' });
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setStatus({ loading: false, error: 'שגיאה בטעינת הקובץ' });
      }
    })();

    return () => {
      cancelled = true;
      aborter.abort();
    };
  }, [url]);

  // Always render the host element; overlay a loader instead of swapping it out
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {status.loading && (
        <div
          style={{
            position: 'absolute', inset: 0, display: 'grid',
            placeItems: 'center', background: 'transparent'
          }}
        >
          טוען…
        </div>
      )}
      {status.error && (
        <div
          style={{
            position: 'absolute', inset: 0, display: 'grid',
            placeItems: 'center', color: 'crimson', background: 'transparent'
          }}
        >
          {status.error}
        </div>
      )}
      <div
        ref={hostRef}
        style={{ width: '100%', height: '100%', overflow: 'auto', padding: '1rem' }}
      />
    </div>
  );
}


/** ---------- Simple preview modal ---------- */
function FilePreviewModal({ open, url, kind, onClose, title }) {
  // Close on ESC
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`תצוגה מקדימה: ${title || 'קובץ'}`}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: '1rem'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(100%, 1100px)', height: 'min(95vh, 900px)',
          background: 'var(--surface, #fff)', borderRadius: '12px',
          boxShadow: 'var(--shadow, 0 8px 30px rgba(0,0,0,.2))',
          display: 'flex', flexDirection: 'column', overflow: 'hidden'
        }}
      >
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '.5rem .75rem', borderBottom: '1px solid #e5e7eb'
        }}>
          <strong style={{ marginInlineStart: '.25rem' }}>{title || 'קובץ'}</strong>
          <button className="iconbtn" onClick={onClose} aria-label="סגור תצוגה">✕</button>
        </header>

        <div style={{ flex: 1, minHeight: 0 }}>
          {kind === 'pdf'  && (
            <iframe
              title={title || 'תצוגה מקדימה'}
              src={url}
              style={{ width: '100%', height: '100%', border: 0 }}
              loading="lazy"
            />
          )}
          {kind === 'docx' && <DocxPreview key={url} url={url} />}
          {kind !== 'pdf' && kind !== 'docx' && (
            <div style={{ padding: '1rem' }}>
              לא ניתן להציג תצוגה מקדימה. ניתן להוריד את הקובץ ולפתוח מקומית.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TopicFiles({ type }) {
  const { id, topicId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const domain  = (searchParams.get('domain')  || 'toshba').toLowerCase();
  const chapter = searchParams.get('chapter') || '';

  const [tractateName, setTractateName] = useState(location.state?.tractateName || '');
  const [topicName, setTopicName]       = useState(location.state?.topicName || '');
  const [chapterTitle, setChapterTitle] = useState('');

  const [files, setFiles]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [nameLoading, setNameLoading] = useState(!tractateName || !topicName);
  const [error, setError]       = useState('');

  // NEW: preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl]   = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewKind, setPreviewKind] = useState(''); // 'pdf' | 'docx' | 'raw'

  // Fetch files for this category
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/topics/${topicId}/files/?category=${encodeURIComponent(type)}`);
        if (!res.ok) throw new Error('files failed');
        const data = await res.json();
        if (!ignore) setFiles(data);
      } catch {
        if (!ignore) setError('שגיאה בטעינת הקבצים');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [topicId, type]);

  // Fallback names
  useEffect(() => {
    if (tractateName && topicName) { setNameLoading(false); return; }
    let ignore = false;
    (async () => {
      try {
        if (!tractateName) {
          const tr = await fetchWithAuth(`/api/tractates/${id}/`);
          if (tr.ok) {
            const td = await tr.json();
            if (!ignore) setTractateName(td?.name || '');
          }
        }
        if (!topicName) {
          const to = await fetchWithAuth(`/api/topics/${topicId}/`);
          if (to.ok) {
            const t = await to.json();
            if (!ignore) setTopicName(t?.name || '');
          }
        }
      } finally {
        if (!ignore) setNameLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, topicId, tractateName, topicName]);

  // Chapter label
  useEffect(() => {
    if (!chapter) { setChapterTitle(''); return; }
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/tractates/${id}/chapters/?domain=${encodeURIComponent(domain)}`);
        if (!res.ok) throw new Error('chapters failed');
        const data = await res.json();
        const chId = String(chapter);
        const match = (data || []).find(c => String(c.id) === chId);
        const title =
          match?.display?.trim()
          || match?.title?.trim()
          || (match?.number != null ? `פרק ${match.number}` : '');
        if (!ignore) setChapterTitle(title || '');
      } catch {
        if (!ignore) setChapterTitle('');
      }
    })();
    return () => { ignore = true; };
  }, [id, domain, chapter]);

  const label = LABELS[type] || 'קבצים';
  const domText = domain === 'talmud' ? 'תלמוד' : 'תושב"ע';
  const tractateCrumbLabel = tractateName ? `${tractateName} (${domText})` : 'מסכת';
  const tractatesHref = `/tractates?domain=${domain}`;
  const tractateHref = `/tractates/${id}?domain=${domain}`;
  const topicsHref   = `/tractates/${id}/topics?domain=${domain}${chapter ? `&chapter=${encodeURIComponent(chapter)}` : ''}`;
  const topicHref    = `/tractates/${id}/topics/${topicId}?domain=${domain}${chapter ? `&chapter=${encodeURIComponent(chapter)}` : ''}`;

  const fileNameFromUrl = (url) => {
    try {
      const u = new URL(url, window.location.origin);
      return decodeURIComponent(u.pathname.split('/').pop() || 'קובץ');
    } catch {
      return 'קובץ';
    }
  };

  return (
    <div className="container">
      <Breadcrumbs
        items={[
          { to: tractatesHref, label: 'מסכתות' },
          { to: tractateHref, label: tractateCrumbLabel },
          { to: topicsHref, label: chapterTitle ? `סוגיות ${chapterTitle}` : 'סוגיות' },
          { to: topicHref, label: topicName || 'סוגיה' },
          { label },
        ]}
      />

      <div className="toolbar toolbar--center">
        <h2 style={{ marginInlineStart: 0 }}>
          {label}{topicName ? ` — ${topicName}` : ''}
        </h2>
      </div>

      <p className="center" style={{ marginTop: '.5rem' }}>
        <Link to={topicHref} state={{ tractateName, topicName }}>חזרה לסוגיה</Link>
        {' · '}
        <Link to={topicsHref} state={{ tractateName }}>חזרה לרשימת הסוגיות</Link>
      </p>

      {(loading || nameLoading) && <p className="muted">טוען…</p>}
      {error && <p style={{ color: 'crimson' }}>{error}</p>}

      {!loading && !error && files.length === 0 && (
        <p className="muted">אין כרגע קבצים להצגה.</p>
      )}

      {!loading && !error && files.length > 0 && (
        <div className="grid-autofit">
          {files.map((f) => {
            const title = f.name || fileNameFromUrl(f?.file || '');
            const preview = buildPreviewUrl(f?.file || '');
            const downloadName = fileNameFromUrl(f?.file || '');
            return (
              <article key={f.id} className="c-card">
                {f.image ? (
                  <img
                    className="c-card__media"
                    src={f.image}
                    alt={title}
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="c-card__media" />
                )}

                <div className="c-card__body">
                  <h3 className="c-card__title">{title}</h3>
                  <p className="c-card__meta">חומרי לימוד — {label}</p>

                  {/* Actions: side-by-side */}
                  <div style={{ marginTop: '0.75rem', display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                    {/* פתיחה → preview modal */}
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        setPreviewTitle(title);
                        setPreviewUrl(preview.src);
                        setPreviewKind(preview.kind);
                        setPreviewOpen(true);
                      }}
                    >
                      פתיחה
                    </button>

                    {/* הורדה → force download with a good name */}
                    <a
                      className="btn"
                      href={f.file}
                      download={downloadName}
                      rel="noreferrer"
                    >
                      הורדה
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <FilePreviewModal
        open={previewOpen}
        url={previewUrl}
        kind={previewKind}
        title={previewTitle}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
}


{/*
  
// pages/TopicFiles.js
import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useLocation, Link } from 'react-router-dom';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';

const LABELS = {
  PREP: 'הכנת הסוגיה',
  TOOLS: 'כלים להוראה',
  EVAL: 'הערכה ומשחוק',
  VISUAL_TALMUD: 'אמצעים חזותיים - תרשימים, תמונות וסרטונים',
  TOSFOT_IYYUN: 'תוספות וסוגיות בעיון',
  SIX_ACHIEVEMENTS: 'ששת ההישגים',
};

export default function TopicFiles({ type }) {
  const { id, topicId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const domain  = (searchParams.get('domain')  || 'toshba').toLowerCase();
  const chapter = searchParams.get('chapter') || '';

  // Names from navigation state (preferred), with fallback fetches:
  const [tractateName, setTractateName] = useState(location.state?.tractateName || '');
  const [topicName, setTopicName]       = useState(location.state?.topicName || '');
  const [chapterTitle, setChapterTitle] = useState('');

  const [files, setFiles]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [nameLoading, setNameLoading] = useState(!tractateName || !topicName);
  const [error, setError]       = useState('');

  // Fetch files for this category
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/topics/${topicId}/files/?category=${encodeURIComponent(type)}`);
        if (!res.ok) throw new Error('files failed');
        const data = await res.json();
        if (!ignore) setFiles(data);
      } catch {
        if (!ignore) setError('שגיאה בטעינת הקבצים');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [topicId, type]);

  // Fallback: fetch names if not passed in state (deep links)
  useEffect(() => {
    if (tractateName && topicName) { setNameLoading(false); return; }
    let ignore = false;
    (async () => {
      try {
        if (!tractateName) {
          const tr = await fetchWithAuth(`/api/tractates/${id}/`);
          if (tr.ok) {
            const td = await tr.json();
            if (!ignore) setTractateName(td?.name || '');
          }
        }
        if (!topicName) {
          const to = await fetchWithAuth(`/api/topics/${topicId}/`);
          if (to.ok) {
            const t = await to.json();
            if (!ignore) setTopicName(t?.name || '');
          }
        }
      } finally {
        if (!ignore) setNameLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, topicId, tractateName, topicName]);

  // Fetch chapter title (for breadcrumb) if chapter param exists
  useEffect(() => {
    if (!chapter) { setChapterTitle(''); return; }
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/tractates/${id}/chapters/?domain=${encodeURIComponent(domain)}`);
        if (!res.ok) throw new Error('chapters failed');
        const data = await res.json();
        const chId = String(chapter);
        const match = (data || []).find(c => String(c.id) === chId);
        const title =
          match?.display?.trim()
          || match?.title?.trim()
          || (match?.number != null ? `פרק ${match.number}` : '');
        if (!ignore) setChapterTitle(title || '');
      } catch {
        if (!ignore) setChapterTitle('');
      }
    })();
    return () => { ignore = true; };
  }, [id, domain, chapter]);

  const label = LABELS[type] || 'קבצים';

  // Domain-aware breadcrumb bits
  const domText = domain === 'talmud' ? 'תלמוד' : 'תושב"ע';
  const tractateCrumbLabel = tractateName ? `${tractateName} (${domText})` : 'מסכת';
  const tractatesHref = `/tractates?domain=${domain}`;

  // Links that preserve the current context
  const tractateHref = `/tractates/${id}?domain=${domain}`;
  const topicsHref   = `/tractates/${id}/topics?domain=${domain}${chapter ? `&chapter=${encodeURIComponent(chapter)}` : ''}`;
  const topicHref    = `/tractates/${id}/topics/${topicId}?domain=${domain}${chapter ? `&chapter=${encodeURIComponent(chapter)}` : ''}`;

  return (
    <div className="container">
      <Breadcrumbs
        items={[
          { to: tractatesHref, label: 'מסכתות' },                         // ← include domain in list link
          { to: tractateHref, label: tractateCrumbLabel },                // ← show (תלמוד)/(תושב"ע)
          { to: topicsHref, label: chapterTitle ? `סוגיות ${chapterTitle}` : 'סוגיות' },
          { to: topicHref, label: topicName || 'סוגיה' },
          { label },
        ]}
      />

      <div className="toolbar toolbar--center">
        <h2 style={{ marginInlineStart: 0 }}>
          {label}{topicName ? ` — ${topicName}` : ''}
        </h2>
      </div>

      //Back links (both keep current domain/chapter)
      <p className="center" style={{ marginTop: '.5rem' }}>
        <Link to={topicHref} state={{ tractateName, topicName }}>
          חזרה לסוגיה
        </Link>
        {' · '}
        <Link to={topicsHref} state={{ tractateName }}>
          חזרה לרשימת הסוגיות
        </Link>
      </p>

      {(loading || nameLoading) && <p className="muted">טוען…</p>}
      {error && <p style={{ color: 'crimson' }}>{error}</p>}

      {!loading && !error && files.length === 0 && (
        <p className="muted">אין כרגע קבצים להצגה.</p>
      )}

      {!loading && !error && files.length > 0 && (
        <div className="grid-autofit">
          {files.map((f) => (
            <article key={f.id} className="c-card">
              {f.image ? (
                <img className="c-card__media" src={f.image} alt={f.name || 'קובץ'} />
              ) : (
                <div className="c-card__media" />
              )}
              <div className="c-card__body">
                <h3 className="c-card__title">{f.name || 'קובץ'}</h3>
                <p className="c-card__meta">חומרי לימוד — {label}</p>
                <div style={{ marginTop: '0.75rem' }}>
                  <a className="btn" href={f.file} target="_blank" rel="noreferrer">
                    פתיחת קובץ
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

  */}